import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, LogOut, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { BetaBadge, BetaCodeLink, BetaEndingBanner } from '../account/BetaCode.tsx';
import { DEMO_BILLING_NOTE, useIsDemo } from '../account/beta.ts';
import { entitlementFor, planFor, useAccount, walletFor } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { deleteAccountFlow, signOutFlow } from '../app/accountActions.ts';
import { useLiveQuery } from '../app/useLiveQuery.ts';
import { formatDate, timeAgo } from '../lib/format.ts';
import { has, planName } from '../plans/features.ts';
import { openPaywallFor, useFlags } from '../plans/gate.ts';
import { usePlans } from '../plans/store.ts';
import { services } from '../services/index.ts';
import { toast } from '../state/feedback.ts';
import { db } from '../storage/db.ts';
import { useSync } from '../sync/service.ts';
import { Button } from '../ui/Button.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { LockBadge } from '../ui/LockBadge.tsx';
import { Section } from '../ui/Section.tsx';

function Card({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-3 rounded-md bg-surface p-4">{children}</div>;
}

export function AccountScreen() {
  const navigate = useNavigate();
  const { session, me, offline, meError, activeScope, ready } = useAccount(
    useShallow((s) => ({
      session: s.session,
      me: s.me,
      offline: s.offline,
      meError: s.meError,
      activeScope: s.activeScope,
      ready: s.ready,
    })),
  );
  const sync = useSync(useShallow((s) => ({ status: s.status, lastSyncAt: s.lastSyncAt, lastError: s.lastError })));
  const trash =
    useLiveQuery(
      () => (session ? db().trash.where('userId').equals(session.userId).toArray() : Promise.resolve([])),
      session?.userId ?? '',
    ) ?? [];
  const catalog = usePlans((p) => p.catalog);
  const demo = useIsDemo();
  const canSync = has(useFlags(me?.personalScope ?? null), 'sync');

  useEffect(() => {
    if (ready && !session) void navigate({ to: '/signin', replace: true });
    if (session) void services.account.refreshMe();
  }, [ready, session, navigate]);
  if (!session) return <div className="min-h-dvh bg-bg" />;

  const scopes = me ? [{ id: me.personalScope, name: 'Personal', role: 'owner' as const }, ...me.orgs] : [];

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton icon={ArrowLeft} label="Back" onClick={() => void navigate({ to: '/' })} />
        <h1 className="t-title">Account</h1>
      </div>
      <div className="mx-auto flex max-w-[760px] flex-col gap-6 p-4">
        <Card>
          <span className="t-heading">{session.email ?? 'Signed in'}</span>
          <span className="t-caption text-dim">
            {session.kind === 'dev' ? 'Dev sign-in · ' : ''}
            {me
              ? `Member since ${formatDate(Date.parse(me.user.createdAt))}`
              : meError
                ? `Couldn’t load your account: ${meError.message}`
                : offline
                  ? 'Offline — showing saved account details'
                  : 'Loading…'}
          </span>
          <BetaBadge />
          {demo ? <span className="t-caption text-dim">Demo account. {DEMO_BILLING_NOTE}</span> : null}
        </Card>
        <BetaEndingBanner />

        <Section title="Workspaces">
          <div className="flex flex-col gap-2">
            {scopes.map((s) => {
              const planLabel = planName(planFor(me, s.id), catalog);
              const wallet = walletFor(me, s.id);
              const ent = entitlementFor(me, s.id);
              const active = (activeScope ?? me?.personalScope) === s.id;
              return (
                <div key={s.id} className="flex flex-col gap-2">
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => services.account.setActiveScope(s.id)}
                    className={`flex flex-col gap-1 rounded-md border p-3 text-left ${active ? 'border-accent bg-accent-soft' : 'border-border bg-surface'}`}
                  >
                    <span className="flex items-center justify-between">
                      <span className="t-heading">{s.name}</span>
                      <span className="t-caption text-dim">{s.id === me?.personalScope ? '' : s.role}</span>
                    </span>
                    <span className="t-caption text-dim">
                      {planLabel} plan{ent?.trialEndsAt ? ' (trial)' : ''}
                      {ent?.seats ? ` · ${ent.seats} seats` : ''}
                      {wallet?.memberAllowance != null
                        ? ` · ${wallet.memberAllowanceLeft ?? 0} of your ${wallet.memberAllowance} monthly AI credits left${wallet.purchased ? `, ${wallet.purchased} shared pack credits` : ''}`
                        : wallet
                          ? ` · ${wallet.balance} AI credits`
                          : ''}
                      {wallet?.memberAllowance == null && wallet?.allowanceExpiresAt
                        ? ` (${wallet.allowance} monthly until ${formatDate(Date.parse(wallet.allowanceExpiresAt))})`
                        : ''}
                      {wallet?.rollover
                        ? ` · ${wallet.rollover} rolled over${wallet.rolloverExpiresAt ? ` until ${formatDate(Date.parse(wallet.rolloverExpiresAt))}` : ''}`
                        : ''}
                      {wallet?.beta ? ` · ${wallet.beta} beta` : ''}
                    </span>
                    {ent?.sandbox ? (
                      <span className="t-caption text-muted" data-testid="sandbox-note">
                        Sandbox purchase (test store or Stripe test mode)
                      </span>
                    ) : null}
                  </button>
                  {s.id !== me?.personalScope ? (
                    <Link
                      to="/team/$orgId"
                      params={{ orgId: s.id }}
                      className="t-label -mt-1 self-start px-3 text-accent"
                    >
                      {s.role === 'owner' || s.role === 'admin' ? 'Manage company' : 'Company details'}
                    </Link>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void navigate({ to: '/team/new' })}>
              Start a company workspace
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void navigate({ to: '/branding' })}>
              Quote branding
            </Button>
          </div>
          <Link to="/plans" className="t-label self-start text-accent">
            See plans and upgrade
          </Link>
          <BetaCodeLink />
        </Section>

        <Section title="Sync">
          <Card>
            {!canSync ? (
              <button
                type="button"
                className="flex items-center justify-between gap-3 text-left"
                onClick={() => openPaywallFor('sync')}
              >
                <span className="t-caption text-dim">
                  Projects stay on this device. Sync across your devices with a paid plan.
                </span>
                <LockBadge flag="sync" />
              </button>
            ) : null}
            <span className="t-label">
              {sync.status === 'syncing'
                ? 'Syncing…'
                : sync.status === 'offline'
                  ? 'Offline — changes are saved on this device and will sync later'
                  : sync.status === 'error'
                    ? `Last sync failed: ${sync.lastError}`
                    : sync.lastSyncAt
                      ? `Synced ${timeAgo(sync.lastSyncAt)}`
                      : 'Not synced yet'}
            </span>
            <Button size="sm" icon={RefreshCw} className="self-start" onClick={() => void services.sync.syncNow()}>
              Sync now
            </Button>
          </Card>
        </Section>

        {trash.length > 0 ? (
          <Section title="Recently deleted">
            <div className="flex flex-col divide-y divide-border rounded-md bg-surface">
              {trash.map((t) => (
                <div key={t.id} className="flex min-h-11 items-center gap-3 px-4">
                  <span className="t-body flex-1 truncate">{t.name || formatDate(t.deletedAt)}</span>
                  <span className="t-caption text-dim">{timeAgo(t.deletedAt)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={RotateCcw}
                    onClick={async () => {
                      try {
                        await services.sync.restore(t.id);
                        toast('Project restored', 'success');
                      } catch (e) {
                        toast(apiMessage(e, 'Could not restore the project.'), 'error');
                      }
                    }}
                  >
                    Restore
                  </Button>
                </div>
              ))}
            </div>
            <p className="t-caption text-muted">Deleted projects can be restored for 30 days.</p>
          </Section>
        ) : null}

        <Section title="Account">
          <div className="flex flex-wrap gap-2">
            <Button icon={LogOut} onClick={async () => (await signOutFlow()) && navigate({ to: '/' })}>
              Sign out
            </Button>
            <Button
              variant="danger"
              icon={Trash2}
              onClick={async () => (await deleteAccountFlow()) && navigate({ to: '/' })}
            >
              Delete account
            </Button>
          </div>
        </Section>
      </div>
    </main>
  );
}
